import { Glass } from "@coach/ui";
import { ChevronRight, Users, X } from "lucide-react";
import { toneOf } from "../../lib/position-tone.js";
import type { PlayerRow } from "../../repos/players.js";

interface RosterPanelProps {
  players: PlayerRow[];
  onAdd: (player: PlayerRow) => void;
  isOnPitch: (playerId: string) => boolean;
  open: boolean;
  onClose: () => void;
}

/**
 * Right-docked roster panel. Visible only when the Player tool is active.
 * Tapping a player adds a player-token near the center of the pitch.
 */
export function RosterPanel({ players, onAdd, isOnPitch, open, onClose }: RosterPanelProps) {
  if (!open) return null;
  return (
    <div className="pointer-events-auto absolute right-3 top-3 bottom-3 z-20 w-72">
      <Glass level="floating" padding="sm" className="flex h-full flex-col gap-2">
        <header className="flex items-center justify-between px-1">
          <div className="flex items-center gap-2">
            <Users className="size-4 text-pitch-lime" />
            <h3 className="text-body-sm font-semibold uppercase tracking-wider">Kader</h3>
            <span className="text-[11px] text-muted-foreground">{players.length}</span>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="flex h-7 w-7 items-center justify-center rounded-full bg-white/6 hover:bg-white/10"
            aria-label="Kader schließen"
          >
            <X className="size-3.5" />
          </button>
        </header>

        <div className="flex-1 space-y-1 overflow-y-auto pr-1">
          {players.length === 0 && (
            <p className="px-2 py-6 text-center text-body-sm text-muted-foreground">
              Noch keine Spieler. Lege sie im Spielermanager an.
            </p>
          )}
          {players.map((p) => {
            const onField = isOnPitch(p.id);
            const tone = toneOf(p.position);
            return (
              <button
                key={p.id}
                type="button"
                onClick={() => !onField && onAdd(p)}
                disabled={onField}
                className={`flex w-full items-center gap-2 rounded-2xl border border-white/10 bg-white/3 px-2 py-1.5 text-left transition-colors ${
                  onField ? "opacity-40" : "hover:border-pitch-lime/40 hover:bg-white/6"
                }`}
              >
                <div
                  className="flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded-full border"
                  style={{
                    background: tone.bg,
                    borderColor: tone.ring,
                    color: tone.fg,
                  }}
                >
                  {p.image_ref ? (
                    // eslint-disable-next-line @next/next/no-img-element, jsx-a11y/alt-text
                    <img src={p.image_ref} className="h-full w-full object-cover" alt="" />
                  ) : (
                    <span className="text-[11px] font-bold">
                      {p.shirt_number ?? initials(p.name)}
                    </span>
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="truncate text-body-sm font-semibold">{p.name}</div>
                  <div className="truncate text-[10px] uppercase tracking-wider text-muted-foreground">
                    {p.position ?? "—"}
                    {p.shirt_number != null && ` · #${p.shirt_number}`}
                  </div>
                </div>
                {!onField && <ChevronRight className="size-4 text-muted-foreground" />}
              </button>
            );
          })}
        </div>

        <footer className="px-1 pt-1 text-[10px] uppercase tracking-widest text-muted-foreground">
          Antippen → aufs Feld
        </footer>
      </Glass>
    </div>
  );
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase();
}
